import React from 'react';
import ReportCard from '../components/ReportCard';
import { SHARED_CAUSE } from '../copy';
import { PAIR_LEAD_FILL, PAIR_REST_FILL } from '../theme';
import { barLength, fmtCount, fmtPct, niceCeil, sentenceCaseMarker, share, tip } from '../format';

/**
 * How often two deficiencies are flagged in the same employee. The leading pair is emphasised, because
 * a dominant pair is the finding — it points at one shared cause rather than two separate problems.
 */
const CoOccurrence = ({ data }) => {
  const total = data.cohort.total;
  const labelFor = (key) => {
    const marker = data.markers.find((entry) => entry.key === key);
    return marker ? marker.label : key;
  };

  const pairs = (data.co_occurrence || []).map((entry) => ({
    ...entry,
    label: entry.pair.map(labelFor).join(' + '),
    prevalence: share(entry.count, total),
  }));
  const axisMax = niceCeil(pairs.length ? pairs[0].prevalence : 0);
  const lead = pairs[0];

  return (
    <div className='mt-ds-9 grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1.5fr_1fr]'>
      <ReportCard fileName='condition-co-occurrence' className='p-[26px]'>
        <h3 className='m-0 font-fig-extrabold text-[26px] font-bold leading-[1.18] text-gray-900'>
          Risk stratification
        </h3>
        <p className='mb-ds-2 mt-[6px] text-xs text-gray-600'>
          How often two conditions are flagged in the same employee (% of workforce)
        </p>

        <div className='grid'>
          {pairs.map((pair, index) => (
            <div
              key={pair.label}
              className={`grid items-center gap-[18px] border-t border-gray-300 py-[15px] ${
                index === pairs.length - 1 ? 'border-b' : ''
              }`}
              style={{ gridTemplateColumns: '210px 1fr 56px' }}
            >
              <div
                className={`text-[14px] ${
                  index === 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-800'
                }`}
              >
                {pair.label}
              </div>
              <div className='rounded-ds-pill bg-gray-200' style={{ height: 15 }}>
                <div
                  data-tip={tip(
                    pair.label,
                    `${fmtPct(pair.prevalence)} of workforce — ${fmtCount(pair.count)} employees`
                  )}
                  className={`h-full rounded-ds-pill ${index === 0 ? PAIR_LEAD_FILL : PAIR_REST_FILL}`}
                  style={{ width: `${barLength(pair.prevalence, axisMax)}%` }}
                />
              </div>
              <div
                className={`text-right font-jetbrains-mono text-[13px] ${
                  index === 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'
                }`}
              >
                {fmtPct(pair.prevalence)}
              </div>
            </div>
          ))}
        </div>
      </ReportCard>

      <div className='rounded-ds-xl border-l-4 border-ds-blue bg-ds-blue-lt p-[26px]'>
        <div className='text-ts-2xs font-semibold uppercase tracking-[0.10em] text-ds-blue-dk'>
          {SHARED_CAUSE.eyebrow}
        </div>
        {lead ? (
          <>
            <h4
              className='mb-ds-3 mt-ds-3 font-fig-extrabold text-[23px] font-bold leading-[1.25] text-gray-900'
              style={{ textWrap: 'pretty' }}
            >
              {`${fmtPct(lead.prevalence)} of employees are flagged for ${sentenceCaseMarker(lead.label)} together`}
            </h4>
            <p className='m-0 mb-[14px] text-ts-md leading-[1.65] text-gray-800'>
              <strong className='font-jetbrains-mono font-semibold'>{fmtPct(lead.prevalence)}</strong>
              {` of the entire workforce is flagged for both — ${fmtCount(lead.count)} employees, far ahead of any other pair.`}
            </p>
          </>
        ) : null}
        <p className='m-0 text-ts-md leading-[1.65] text-gray-800'>{SHARED_CAUSE.body}</p>
      </div>
    </div>
  );
};

export default CoOccurrence;
